import { axios } from "utilities/axios";
import Qs from "qs";
import {
    Route,
    ITokensPostResponse,
    ITokensPostRequest,
    IPermission,
    IClient,
    Permission,
} from "@edgetier/watchtower-types";
import { getRedirectUri } from "utilities/get-redirect-uri";
import { BASE_URL } from "utilities/axios";
import { toast } from "react-hot-toast";
import { PERMISSION_PAGE_MAPPING } from "./authenticate.constants";
import Page from "constants/page";

export const LOCAL_ERROR = "Authentication failed unexpectedly.";

/**
 * Take the authentication details provided by Cognito, storing the access token, and using them to store a refresh
 * token via the backend. It's stored as a HTTP-only cookie which only the backend can do.
 * @returns A token.
 */
export const authenticate = async (search: string) => {
    const parameters = Qs.parse(search, { ignoreQueryPrefix: true });

    if (parameters.error || typeof parameters.code !== "string" || typeof parameters.state !== "string") {
        toast.error(LOCAL_ERROR);
        throw new Error();
    }

    // These must match what's in local storage. This is a security check.
    const authenticationState = localStorage.getItem("authenticationState");
    const codeVerifier = localStorage.getItem("authenticationCodeVerifier");
    if (parameters.state !== authenticationState || typeof codeVerifier !== "string") {
        toast.error(LOCAL_ERROR);
        throw new Error();
    }

    const data: ITokensPostRequest = { code: parameters.code, codeVerifier, redirectUri: getRedirectUri() };
    const requestOptions = { baseURL: BASE_URL };

    // Create a refresh token and store it as a cookie via the backend. Using default Axios rather than our own
    // instance as it never needs authentication headers.
    const response = await axios.post<ITokensPostResponse>(Route.Tokens, data, requestOptions);
    return response.data;
};

/**
 * Using user's role and permission find the appropriate available page to redirect after a successful authentication
 * @param userRolePermissions   Permissions selected for user's role
 */
export const findRedirectPage = (userRolePermissions?: IPermission[], clientDetails?: IClient) => {
    if (Array.isArray(userRolePermissions) && userRolePermissions.length > 0) {
        for (const [permission, page] of Array.from(PERMISSION_PAGE_MAPPING)) {
            const hasPermission = userRolePermissions.some(({ permissionId }) => permission === permissionId);
            if (!clientDetails?.isAgentReviewEnabled && permission === Permission.ViewAgent) {
                continue;
            }
            if (!clientDetails?.isLiveViewEnabled && permission === Permission.ViewLive) {
                continue;
            }
            if (hasPermission) {
                return page;
            }
        }
    }

    return Page.Login;
};
